<template>
  <div class="container">
    <Loading v-show="pageLoading" />
    <div class="width_wrap">
      <MyPageSidebar />
      <div class="body_content sub">
        <!-- 서브 타이틀영역 -->
        <h1 class="sub_subject">진단검사<span></span></h1>
        <div class="glores-B-list-h2">
          <div class="h-left">
            <!-- 필요시 여기에 검색 또는 필터 UI 추가 -->
          </div>
        </div>

        <div
          v-if="listLoading"
          class="loading-indicator"
          style="text-align: center; padding: 20px"
        >
          진단 검사 목록을 불러오는 중...
        </div>
        <div
          v-if="error"
          class="error-message"
          style="text-align: center; padding: 20px; color: red"
        >
          {{ error }}
        </div>

        <div v-if="!listLoading && !error" class="glores-B-list-type1">
          <n-data-table
            v-if="orders.length > 0"
            class="org-order-table"
            :columns="columns"
            :data="orders"
            :pagination="false"
            :bordered="false"
            :single-line="false"
          />
          <ul v-else>
            <li class="none-data">등록된 견적이 없습니다.</li>
          </ul>
        </div>

        <div
          v-if="orders.length > 0"
          class="pagination-controls"
          style="margin-top: 15px; text-align: center"
        >
          <n-button
            @click="wrappedGoToPreviousPage"
            :disabled="currentPage === 1"
            style="margin-right: 8px"
          >
            이전 페이지
          </n-button>
          <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
          <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage">
            다음 페이지
          </n-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h, computed, watch } from "vue";
import MyPageSidebar from "@/views/member/MypageSidebar.vue";
import Banner from "@/components/Banner.vue";
import { USER_ROUTES } from "@/assets/route_constants.ts";
import {
  NDataTable,
  NButton,
  NSpace,
  useMessage,
  NModal,
  NDescriptions,
  NDescriptionsItem,
  NCard,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import { getCallable, makeCallApiParam } from "@/config/env";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import { useRouter } from "vue-router"; // getOrderStatusString removed as it's replaced by graphical steps
import { Order } from "@/model/order";
import { useAuth } from "@/composables/useAuth";
import { useAlertDialog } from "@/composables/useAlert";
import { usePagination } from "@/composables/usePagination";

const useAlert = useAlertDialog();
const { isLoggedIn } = useAuth();
const message = useMessage();
const router = useRouter();
const getCallApiCallable = getCallable("callApi");
const getMyOrders = "getMyOrders";
const searchQuery = ref(""); // 검색어 상태 추가
const pageSize = 10; // 페이지 크기를 상수로 정의

const pageLoading = ref(false);
// const listLoading = ref(false);
// const error = ref<string | null>(null);
// const orders = ref<Order[]>([]);

const showOrderDetailModal = ref(false);
const selectedOrderDetail = ref<Order | null>(null);

const {
  paginatedItems: orders,
  listLoading,
  error,
  currentPage,
  hasNextPage,
  goToNextPage: composableGoToNextPage,
  goToPreviousPage: composableGoToPreviousPage,
  refreshData: composableRefreshItems, // Renamed for clarity
} = usePagination<Order, { searchKey?: string; searchValue?: string }>(
  async (params) =>
    getCallApiCallable(
      makeCallApiParam(getMyOrders, {
        ...params,
        searchKey: "title",
        searchValue: searchQuery.value,
        type: constant.orderType.BODY,
      })
    ),
  pageSize
);

watch(
  isLoggedIn,
  async (newValue) => {
    if (newValue === false) {
      await useAlert("회원전용 페이지 입니다. 로그인 후 이용해주세요.");
      // TODO : 단순히 뒤로 갈지 향후 확인 후 변경 가능.
      // router.back();
      router.push({ name: USER_ROUTES.Home.Name });
    }
  },
  { immediate: true }
);

const statusSteps = [
  {
    key: "register_pending",
    label: "진단정보 등록대기",
    statuses: [
      constant.orderStatus.APPROVED,
      constant.orderStatus.APPROVED_TEMP_SAVE,
    ],
    iconText: "1",
    color: "#66BB6A", // 녹색 (Green)
  },
  {
    key: "start_pending",
    label: "진단시작 대기",
    statuses: [constant.orderStatus.READY],
    iconText: "2",
    color: "#26A69A", // 청록색 (Teal)
  },
  {
    key: "in_progress",
    label: "진단 진행중",
    statuses: [constant.orderStatus.ING],
    iconText: "3",
    color: "#42A5F5", // 파란색 (Blue)
  },
  {
    key: "completed",
    label: "진단 완료",
    statuses: [constant.orderStatus.COMPLETED],
    iconText: "4",
    color: "#5C6BC0", // 남색 (Indigo)
  },
];

const columns = computed(() => [
  {
    title: "견적명",
    key: "name",
    ellipsis: { tooltip: true },
    titleAlign: "center",
    align: "left",
  },
  {
    title: "신청일",
    key: "createdDate",
    titleAlign: "center",
    align: "center",
    width: 120,
    render: (row: Order) =>
      common.getYYYYMMDD2(new Date(Number(row.createdDate))), // formatDate 함수 필요
  },
  {
    title: "진행상태",
    key: "progressManagementGroup",
    titleAlign: "center",
    children: [
      {
        title: "",
        key: "status",
        titleAlign: "center",
        align: "center",
        width: 300,
        render: (row: Order) => {
          if (!hasSurvey(row)) {
            return h("span", "해당없음");
          }

          let actualCurrentStepIndex = -1;
          statusSteps.forEach((step, index) => {
            if (step.statuses.includes(row.status)) {
              actualCurrentStepIndex = index;
            }
          });

          const stepNodes: any[] = [];
          statusSteps.forEach((step, index) => {
            let stepClass = "pending";
            let iconStyle = {};
            let labelStyle = {};

            if (index < actualCurrentStepIndex) {
              // Completed step
              stepClass = "completed";
              iconStyle = {
                borderColor: step.color,
                backgroundColor: step.color,
                color: "white",
              };
              labelStyle = { color: step.color };
            } else if (index === actualCurrentStepIndex) {
              // Current step
              stepClass = "current";
              iconStyle = {
                borderColor: step.color,
                backgroundColor: step.color,
                color: "white",
              };
              labelStyle = { color: step.color, fontWeight: "bold" };
            }

            stepNodes.push(
              h("div", { class: `step-item ${stepClass}` }, [
                h(
                  "div",
                  { class: "step-icon-container", style: iconStyle },
                  step.iconText
                ),
                h(
                  "div",
                  { class: "step-label", style: labelStyle },
                  step.label
                ),
              ])
            );

            if (index < statusSteps.length - 1) {
              const connectorClass =
                index < actualCurrentStepIndex ? "completed" : "pending";
              const connectorStyle =
                index < actualCurrentStepIndex
                  ? { backgroundColor: step.color }
                  : {};
              stepNodes.push(
                h("div", {
                  class: `step-connector ${connectorClass}`,
                  style: connectorStyle,
                })
              );
            }
          });
          return h("div", { class: "status-stepper" }, stepNodes);
        },
      },
      {
        title: "", // 하위 헤더 텍스트를 빈 문자열로 설정하여 숨김
        key: "actions",
        width: 150,
        titleAlign: "center",
        align: "center",
        render(row: Order) {
          const buttons: any = [];
          const hasSurveyItem = hasSurvey(row);

          if (hasSurveyItem) {
            const currentStep = statusSteps.find((step) =>
              step.statuses.includes(row.status)
            );
            const buttonColor = currentStep ? currentStep.color : undefined;

            if (
              row.status === constant.orderStatus.APPROVED ||
              row.status === constant.orderStatus.APPROVED_TEMP_SAVE
            ) {
              buttons.push(
                h(
                  NButton,
                  {
                    size: "small",
                    color: buttonColor,
                    onClick: () => gotoOrderProject(row.SK),
                  },
                  { default: () => "진단정보 등록" }
                )
              );
            } else if (row.status === constant.orderStatus.READY) {
              buttons.push(
                h(
                  NButton,
                  {
                    size: "small",
                    color: buttonColor,
                    onClick: () => gotoOrderProjectDetail(row.SK),
                  },
                  { default: () => "진단정보 확인" }
                )
              );
            } else if (row.status === constant.orderStatus.ING) {
              buttons.push(
                h(
                  NButton,
                  {
                    size: "small",
                    color: buttonColor,
                    onClick: () => gotoOrderProjectDetail(row.SK),
                  },
                  { default: () => "진단현황 확인" }
                )
              );
            } else if (row.status === constant.orderStatus.COMPLETED) {
              buttons.push(
                h(
                  NButton,
                  {
                    size: "small",
                    color: buttonColor,
                    onClick: () => gotoOrderProjectDetail(row.SK),
                  },
                  { default: () => "진단결과 조회" }
                )
              );
            }
          }

          return h(NSpace, { justify: "center" }, () => buttons);
        },
      },
    ],
  },
]);

async function wrappedRefreshItems() {
  await composableRefreshItems();
}

async function wrappedGoToNextPage() {
  if (hasNextPage.value) {
    await composableGoToNextPage();
  }
}

async function wrappedGoToPreviousPage() {
  if (currentPage.value > 1) {
    await composableGoToPreviousPage();
  }
}

function hasSurvey(order: Order) {
  let hasSurveyItem = false;
  for (let product in order.products) {
    if (
      order.products[product].type?.startsWith("putt_") ||
      order.products[product].type?.startsWith("tocc_") ||
      order.products[product].type?.startsWith("tec")
    ) {
      hasSurveyItem = true;
      break;
    }
  }
  return hasSurveyItem;
}

// async function fetchUserOrders() {
//     listLoading.value = true;
//     error.value = null;
//     try {
//         const result = await getUserOrdersCallable({ type: constant.orderType.BODY });
//         orders.value = common.getItemListFromFunctionResult(result) as Order[];
//     } catch (err: any) {
//         console.error("사용자 견적 목록 로딩 실패:", err);
//         error.value = "견적 목록을 불러오는데 실패했습니다.";
//         orders.value = [];
//     } finally {
//         listLoading.value = false;
//     }
// }

function viewOrderDetails(order: Order) {
  selectedOrderDetail.value = order;
  showOrderDetailModal.value = true;
}

function gotoOrderProject(orderSK: string) {
  router.push({
    name: USER_ROUTES.Mypage.Order.Org.Project.Name,
    params: { SK: orderSK },
  });
}

function gotoOrderProjectDetail(orderSK: string) {
  router.push({
    name: USER_ROUTES.Mypage.Order.Org.Project.Detail.Name,
    params: { SK: orderSK },
  });
}

onMounted(() => {
  wrappedRefreshItems();
});
</script>

<style scoped>
.product-item-card-modal {
  border: 1px solid #eee;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  background-color: #f9f9f9;
}

/* Status Stepper Styles */
.org-order-table :deep(.status-stepper) {
  display: flex !important; /* !important를 추가하여 우선순위를 높여봅니다. */
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding: 5px 0;
  box-sizing: border-box; /* 패딩과 테두리가 너비에 포함되도록 */
}

.org-order-table :deep(.step-item) {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  min-width: 60px; /* 각 스텝의 최소 너비, 필요시 조절 */
  padding: 0 2px; /* 좌우 약간의 패딩으로 너무 붙는 것 방지 */
  box-sizing: border-box;
}

.org-order-table :deep(.step-icon-container) {
  width: 24px; /* 아이콘 크기 약간 증가 */
  height: 24px;
  border-radius: 50%;
  border: 2px solid #e0e0e0; /* Default pending border */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
  background-color: #f0f0f0; /* Default pending background */
  color: #aaa; /* Default pending text */
  font-size: 12px;
  font-weight: bold;
  transition: background-color 0.3s, border-color 0.3s, color 0.3s;
  flex-shrink: 0; /* 아이콘 크기 고정 */
}

.org-order-table :deep(.step-label) {
  font-size: 10px; /* 라벨 폰트 크기 약간 줄임 */
  color: #aaa; /* Default pending label */
  word-break: keep-all;
  line-height: 1.2;
  margin-top: 2px;
}
.org-order-table :deep(.step-connector) {
  flex-grow: 1;
  height: 2px;
  background-color: #e0e0e0; /* Default pending connector */
  margin: 0 1px; /* 연결선 좌우 마진 약간 줄임 */
  position: relative;
  top: 12px; /* 아이콘 높이(24px)의 중앙에 맞춤 */
  transition: background-color 0.3s;
  min-width: 5px; /* 연결선이 너무 짧아지지 않도록 최소 너비 */
}
/*
  "진행상태" 그룹 헤더 아래의 자식 컬럼("상태", "관리") 헤더 셀을 완전히 숨기기 위한 스타일입니다.
  아래 선택자(예: th[data-col-key="status"])는 Naive UI가 <th> 요소에 컬럼 key를 기반으로
  data-attribute를 추가한다고 가정한 것입니다.
  실제 적용되는 클래스나 속성은 브라우저 개발자 도구로 확인 후 수정해야 합니다.
*/
.org-order-table :deep(thead th[data-col-key="status"]),
.org-order-table :deep(thead th[data-col-key="actions"]) {
  padding: 0 !important;
  border: none !important;
  height: 0 !important; /* 셀의 높이를 0으로 만듭니다. */
  line-height: 0 !important; /* line-height로 인해 높이가 생기는 것을 방지합니다. */
  font-size: 0 !important; /* 폰트 크기를 0으로 하여 공간 차지를 최소화합니다. */
  overflow: hidden !important; /* 내용이 넘칠 경우 숨깁니다. */
  /*
      visibility: hidden; 을 사용할 수도 있지만, 이 경우 셀이 공간은 차지하되 보이지만 않게 됩니다.
      위의 스타일들은 셀이 시각적으로 공간을 거의 차지하지 않도록 하는 데 더 효과적입니다.
      display: none; 은 테이블 레이아웃을 깨뜨릴 수 있으므로 권장하지 않습니다.
    */
}

.sub_title_01 p {
  position: absolute;
  top: 70px;
  left: 0;
  margin-top: -15px;

  width: 100%;
  font-size: 30px;
  color: #fff;
  text-align: center;
  font-family: "Noto Sans", "Nanum Gothic", "Malgun Gothic", sans-serif;
  font-weight: 600;
}
.sub_title_01 h2 {
  position: absolute;
  top: 130px;
  left: 0;
  margin-top: -15px;

  width: 100%;
  font-size: 19px;
  color: #fff;
  text-align: center;
  font-family: "Noto Sans", "Nanum Gothic", "Malgun Gothic", sans-serif;
  font-weight: 500;
  line-height: 1.7em;
}

.sub_scroll_wrap {
  overflow: hidden;
  height: 39px;
  border-bottom: 1px solid #ccc;
  background: #fff;
}
.sub_scroll_wrap > div {
  position: relative; /*padding: 0 30px;*/
}
.sub_scroll_wrap > div > div {
  overflow-x: scroll;
}
.sub_scroll_wrap > div > div .scroll_nav {
  display: inline-block;
  min-width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  list-style: none;
  font-size: 0;
  text-align: center;
  white-space: nowrap;
}
.sub_scroll_wrap > div > div .scroll_nav li {
  display: inline-block;
}
.sub_scroll_wrap > div > div .scroll_nav li a {
  display: inline-block;
  height: 39px;
  line-height: 39px;
  padding: 0 12px;
  font-size: 13px;
  color: #bbb;
  font-family: "Montserrat", "Noto Sans", NanumGothic, "나눔고딕", "Open Sans",
    sans-serif;
  box-sizing: border-box;
  font-weight: 600;
}
.sub_scroll_wrap > div > div .scroll_nav li.on a {
  color: #be7b42;
  font-weight: 600;
}
.sub_scroll_wrap > div i {
  position: absolute;
  left: 0;
  top: 0;
  width: 30px;
  height: 39px;
}
.sub_scroll_wrap > div i.prev:before {
  content: "";
  position: absolute;
  top: 0;
  left: 3px;
  bottom: 0;
  width: 0;
  height: 0;
  margin: auto;
  border: 6px solid transparent;
  border-right-color: #333;
}
.sub_scroll_wrap > div i.next {
  right: 0;
  left: inherit;
}
.sub_scroll_wrap > div i.next:before {
  content: "";
  position: absolute;
  top: 0;
  right: 3px;
  bottom: 0;
  width: 0;
  height: 0;
  margin: auto;
  border: 6px solid transparent;
  border-left-color: #333;
}
@media screen and (min-width: 768px) {
  .sub_scroll_wrap > div i {
    display: none;
  }
}
@media screen and (min-width: 1200px) {
  .sub_scroll_wrap {
    overflow: visible;
    height: auto;
    border-bottom: none;
    background: #fff;
    box-shadow: 3px 3px 3px rgb(0 0 0 / 10%);
  }
  .sub_scroll_wrap > div {
    position: relative;
    padding: 0;
  }
  .sub_scroll_wrap > div > div {
    overflow: hidden;
  }
  .sub_scroll_wrap > div > div .scroll_nav {
    display: table;
    margin: 0;
    width: 100%;
    padding: 0;
    white-space: normal;
    text-align: left;
    border-collapse: collapse;
  }
  .sub_scroll_wrap > div > div .scroll_nav li {
    display: table-cell;
    width: 33%;
    position: relative;
  }
  .sub_scroll_wrap > div > div .scroll_nav li:after {
    content: "";
    display: block;
    position: absolute;
    top: 50%;
    right: 0;
    background: #bbb;
    width: 1px;
    height: 15px;
    margin-top: -7px;
  }
  .sub_scroll_wrap > div > div .scroll_nav li:last-child:after {
    display: none;
  }
  .sub_scroll_wrap > div > div .scroll_nav li:before {
    position: absolute;
    visibility: hidden;
    left: 0;
    bottom: 0px;
    right: 0px;
    width: 100%;
    height: 3px;
    background: #be7b42;
    content: "";
    -webkit-transform: scaleX(0);
    transform: scaleX(0);
    -webkit-transition: all 0.2s ease-in-out 0s;
    transition: all 0.2s ease-in-out 0s;
    opacity: 0.85;
  }
  .sub_scroll_wrap > div > div .scroll_nav li a {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    height: 60px;
    line-height: 60px;
    padding: 0 5px;
    font-size: 16px;
    text-align: center;
    color: #bbbbbb;
    font-weight: 600;
  }
  .sub_scroll_wrap > div > div .scroll_nav li a:hover {
    color: #be7b42;
  }
  .sub_scroll_wrap > div > div .scroll_nav li.on a {
    color: #be7b42;
  }
  .sub_scroll_wrap > div > div .scroll_nav li.on:before,
  .sub_scroll_wrap > div > div .scroll_nav li:hover:before {
    visibility: visible;
    -webkit-transform: scaleX(1);
    transform: scaleX(1);
  }
}

.sub04_wrap * {
  font-family: "Montserrat", "Noto Sans", NanumGothic, "나눔고딕", "Open Sans",
    sans-serif;
  box-sizing: border-box;
  word-break: keep-all;
}
.sub04_wrap > ul {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.sub04_wrap > ul li {
  width: 48%;
}
.sub04_wrap > ul li figure {
  border: 1px solid #ddd;
}
.sub04_wrap > ul li:nth-child(-n + 2) {
  margin-bottom: 30px;
}
.sub04_wrap > ul li a {
  display: block;
  border-radius: 10px;
  position: relative;
  transition: all.2s;
}
.sub04_wrap > ul li h1 {
  font-size: 14px;
  font-weight: 500;
  padding-top: 15px;
  text-align: center;
}
.sub04_wrap > ul li a > div.bg {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: none;
  opacity: 0;
  transition: all.2s;
}
.sub04_wrap > ul li a > div.bg > span {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  line-height: 80px;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  top: 50%;
  background: #ba7b3d;
  color: #fff;
  font-weight: 500;
}
@media screen and (min-width: 768px) {
  .sub04_wrap > ul {
    justify-content: normal;
  }
  .sub04_wrap > ul li h1 {
    font-size: 16px;
  }
  .sub04_wrap > ul li {
    width: 23.5%;
    margin-right: 2%;
  }
  .sub04_wrap > ul li:nth-child(-n + 2) {
    margin-bottom: 0px;
  }
  .sub04_wrap > ul li:last-child {
    margin-right: 0;
  }
  .sub04_wrap > ul li a:hover > div.bg {
    display: block;
    opacity: 1;
  }
}

@media all and (min-width: 768px) {
  .glores-B-list-h2 .glores-B-full-select-btn select[name="myreg_idx"] {
    width: auto;
    min-width: 250px;
  }
}
</style>
